/**
 * CORE-AW — the three answers "is this person working today" actually has,
 * for the BROWSER.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THIS IS A MIRROR, NOT A SECOND OPINION
 * ═══════════════════════════════════════════════════════════════════════════
 * `supabase/functions/_shared/technicianScheduleState.ts` is the doctrine and
 * carries the reasoning; read it there. Everything below this header is a
 * VERBATIM copy of that file from the type declaration onward.
 *
 * It is copied rather than imported because `tsconfig.app.json` includes only
 * `src`, so the browser bundle cannot reach a Deno edge module — the same
 * constraint that produced `src/constants/proficiencyCategories.ts` beside
 * `supabase/functions/_shared/canonicalCategoryMapper.ts`. A copy that drifts
 * is worse than no copy, because it looks authoritative: two halves of one
 * product would answer "is this technician working" differently and neither
 * would be visibly wrong. `tests/unit/capacity/coreAwScheduleStateParity.test.ts`
 * compares the two files byte for byte from the marker below and fails on any
 * divergence.
 *
 * Do not edit this file alone. Edit the doctrine and re-copy.
 */

/** The canonical per-day hours envelope, as `technicians.hours` stores it. */
export interface TechnicianHoursEnvelope {
  hours?: Record<string | number, { open?: string; close?: string } | null | undefined> | null;
  time_off?: string[] | null;
  /**
   * Core-100 R4 W5 (CORE-AW T): the first day of employment (YYYY-MM-DD). A date before it is a FACT about that
   * date — not working — on both runtimes (the server calculator alone used to know it).
   */
  hire_date?: string | null;
}

export type TechnicianScheduleState =
  /** Working today, with known bounds. Capacity is computable. */
  | { state: 'scheduled'; open: string; close: string }
  /**
   * Genuinely not working today. Capacity is zero and that zero is a FACT
   * about the day, not a gap in the record — §44's first clause.
   */
  | { state: 'not_scheduled'; reason: 'day_not_in_schedule' | 'time_off' | 'not_yet_employed' }
  /**
   * The working hours are not known. Capacity MUST NOT be assumed — in either
   * direction. This is NOT zero capacity and NOT an absent technician; it is
   * the absence of an answer, and a consumer is obliged to say so rather than
   * pick one — §44's second clause.
   */
  | { state: 'hours_unknown'; reason: 'never_configured' | 'incomplete_day_entry' };

const DAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday',
  'thursday', 'friday', 'saturday',
] as const;

/** `true` when the envelope carries no schedule at all. */
function hoursNeverConfigured(tech: TechnicianHoursEnvelope): boolean {
  const raw = tech.hours;
  if (raw === null || raw === undefined) return true;
  if (typeof raw !== 'object') return true;
  // An empty map is the shape written by `accept-team-invitation`,
  // `change-member-role` and the commit wizard — a roster row exists and
  // nobody has ever said when this person works.
  return Object.keys(raw).length === 0;
}

/** The entry for one day, accepting numeric ("0".."6") and name keys. */
function dayEntry(
  tech: TechnicianHoursEnvelope,
  dayIndex: number,
): { open?: string; close?: string } | null | undefined {
  const raw = tech.hours;
  if (!raw || typeof raw !== 'object') return undefined;

  const byIndex = raw[dayIndex] ?? raw[String(dayIndex)];
  if (byIndex !== undefined) return byIndex;

  const wanted = DAY_NAMES[dayIndex];
  for (const [k, v] of Object.entries(raw)) {
    if (String(k).toLowerCase() === wanted) return v;
  }
  return undefined;
}

const isBlank = (s: string | undefined | null): boolean =>
  s === undefined || s === null || String(s).trim() === '';

/**
 * Classify one technician's schedule for one shop-local date.
 *
 * `dateStr` is `YYYY-MM-DD` in the SHOP's timezone. The caller owns the
 * conversion — this module deliberately does no timezone work, because getting
 * the zone right is the caller's problem and hiding it here would make two
 * different callers disagree silently.
 *
 * Classification, in order:
 *
 *   1. on a recorded time-off date      → not_scheduled  / time_off
 *   2. no schedule recorded at all      → hours_unknown / never_configured
 *   3. no entry for this weekday, or an
 *      entry with neither open nor close → not_scheduled / day_not_in_schedule
 *   4. entry with exactly one of the two → hours_unknown / incomplete_day_entry
 *   5. otherwise                         → scheduled
 *
 * STEP 1 OUTRANKS STEP 2, and the first version of this module had it the other
 * way round. The reasoning then was that the time-off list says when somebody is
 * NOT working and so cannot establish when they are — true, and beside the
 * point. The question being asked is about ONE DATE. An explicit time-off record
 * does not tell us this person's normal schedule, but it does settle the only
 * thing this call needs: they are unavailable on the date in hand, and somebody
 * said so deliberately. Reporting that as `hours_unknown` would discard a
 * recorded fact in favour of an absence of one.
 *
 * So a technician with `{ hours: {}, time_off: [today] }` is `not_scheduled` on
 * today, and still `hours_unknown` on every other date — which is exactly the
 * shape of what is actually known about them.
 *
 * Step 3 and step 4 are the pair the old readers merged. An empty entry on a
 * configured week is somebody saying "not this day". A half-filled entry is
 * somebody having said nothing usable, and no amount of reading it harder will
 * produce the missing side. The first is a fact; the second is a gap.
 */
export function resolveTechnicianScheduleState(
  tech: TechnicianHoursEnvelope,
  dateStr: string,
): TechnicianScheduleState {
  // Core-100 R4 W5: before the first day of employment nobody is working — a fact, whatever the week says.
  if (tech.hire_date && /^\d{4}-\d{2}-\d{2}$/.test(dateStr) && dateStr < String(tech.hire_date).slice(0, 10)) {
    return { state: 'not_scheduled', reason: 'not_yet_employed' };
  }

  // A recorded absence on THIS date is a fact about this date, and outranks
  // knowing nothing about the surrounding week.
  if ((tech.time_off ?? []).includes(dateStr)) {
    return { state: 'not_scheduled', reason: 'time_off' };
  }

  if (hoursNeverConfigured(tech)) {
    return { state: 'hours_unknown', reason: 'never_configured' };
  }

  // Parse the shop-local calendar date without constructing a UTC instant —
  // `new Date('2026-09-09')` is midnight UTC and shifts the weekday for shops
  // west of Greenwich, which would silently misclassify a whole timezone band.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!m) return { state: 'hours_unknown', reason: 'never_configured' };
  const dayIndex = new Date(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]),
  ).getDay();

  const entry = dayEntry(tech, dayIndex);

  if (entry === undefined || entry === null) {
    return { state: 'not_scheduled', reason: 'day_not_in_schedule' };
  }

  const openBlank = isBlank(entry.open);
  const closeBlank = isBlank(entry.close);

  if (openBlank && closeBlank) {
    return { state: 'not_scheduled', reason: 'day_not_in_schedule' };
  }
  if (openBlank || closeBlank) {
    return { state: 'hours_unknown', reason: 'incomplete_day_entry' };
  }

  return {
    state: 'scheduled',
    open: String(entry.open).slice(0, 5),
    close: String(entry.close).slice(0, 5),
  };
}

/**
 * Operator-facing reason, for the audit row and for a refusal.
 *
 * Kept here so every consumer says the same thing, and so the unknown case
 * cannot be described as absence by a caller writing its own copy — which is
 * exactly how the conflation reached the audit trail in the first place.
 */
export function describeScheduleState(s: TechnicianScheduleState): string {
  switch (s.state) {
    case 'scheduled':
      return `Scheduled ${s.open}–${s.close}`;
    case 'not_scheduled':
      return s.reason === 'time_off'
        ? 'Not scheduled on this date (time off)'
        : s.reason === 'not_yet_employed'
          ? 'Not scheduled on this date (before their first day)'
          : 'Not scheduled on this date (no hours set for this day)';
    case 'hours_unknown':
      return s.reason === 'never_configured'
        ? 'Working hours unknown — no schedule has been set for this technician'
        : 'Working hours unknown — the entry for this day is incomplete';
  }
}

/** Convenience: capacity is computable only in the `scheduled` state. */
export function isCapacityKnown(s: TechnicianScheduleState): boolean {
  return s.state === 'scheduled';
}

/**
 * `true` only where zero capacity is a FACT about the day.
 *
 * The distinction §44 turns on: `not_scheduled` genuinely has no capacity;
 * `hours_unknown` has an unknown capacity, and calling it zero is the
 * assumption the requirement forbids.
 */
export function isZeroCapacityEstablished(s: TechnicianScheduleState): boolean {
  return s.state === 'not_scheduled';
}
